import { useState } from 'react';
import { autoCompleteApi } from 'api/autoCompleteApi';
import { useGlobalContext } from 'contexts/GlobalContext';
import { suggestedSearches } from 'components/PageWrappers/SharedComponents/GlobalSearchModal/suggestedSearches';

import { SearchModal } from 'components/Toolkit/SearchModal/SearchModal';
import { fireToast } from 'helpers/Toasts';
import { pushToDataLayer } from 'services/gtmService';
import { useLastSearch } from 'components/PageWrappers/SharedComponents/LastSearchCard/hooks/useLastSearch';
import type { IAutoCompleteSuggestion } from 'types';
import {
  formatSearchItemTerms,
  generateSearchDescription,
} from 'helpers/formatSearchTerms';
import { IHandleSelectSearchItem } from 'components/Toolkit/SearchModal/BaseSearch';
import { restoreScrollPosition } from 'helpers/scroll';

export interface GlobalSearchModalProps {
  baseUrl: string;
}

const GlobalSearchModal = (props: GlobalSearchModalProps) => {
  const { baseUrl } = props;

  const [autoSuggestValues, setAutoSuggestValues] =
    useState<IAutoCompleteSuggestion[]>();

  const { isGlobalSearchModalOpen, setIsGlobalSearchModalOpen } =
    useGlobalContext();

  const { lastSearchData, setLastSearch } = useLastSearch();

  async function handleInput(text: string) {
    if (text.length > 2) {
      try {
        const { data } = await autoCompleteApi.getAutoCompleteValues(text);
        setAutoSuggestValues(data.suggestions);
      } catch (error) {
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
      }
    } else {
      setAutoSuggestValues(undefined);
    }
  }

  function handleSearch(search: string) {
    const url = `${baseUrl}/all?words=${search}`;
    pushToDataLayer({ event: 'global_search', url });
    pushToDataLayer({
      event: 'keyword_search',
      searchType: 'global_keyword_search',
      searchTerm: search,
    });
    location.assign(url);
    restoreScrollPosition(`${url.replace(baseUrl, '')}`);
    setLastSearch(`All Sections · '${search}'`, url);
  }

  function handleSelectSearchItem({
    autoSuggestedText,
    url,
    searchRequest,
    category,
    tracking,
    searchType,
  }: IHandleSelectSearchItem) {
    const description = generateSearchDescription(
      searchRequest?.makeModelFilters,
      autoSuggestedText,
    );
    if (url) {
      pushToDataLayer({ event: 'global_search', url });
      if (tracking === 'GLOBAL') {
        pushToDataLayer({
          event: 'keyword_search',
          searchType,
          searchTerm: autoSuggestedText,
        });
      }
      restoreScrollPosition(`${url.replace(baseUrl, '')}`);
      location.assign(url);
      setLastSearch(
        formatSearchItemTerms(description, category, autoSuggestedText),
        url,
      );
    } else {
      const formattedUrl = `${baseUrl}/${
        category ?? 'all'
      }?words=${autoSuggestedText}`;
      pushToDataLayer({ event: 'global_search', formattedUrl });
      restoreScrollPosition(`${formattedUrl.replace(baseUrl, '')}`);
      location.assign(formattedUrl);
      setLastSearch(
        formatSearchItemTerms(description, category, autoSuggestedText),
        formattedUrl,
      );
    }
  }

  function handleClose() {
    setAutoSuggestValues(undefined);
    setIsGlobalSearchModalOpen(false);
  }

  return (
    <SearchModal
      isOpen={isGlobalSearchModalOpen}
      askToClose={handleClose}
      onSearch={handleSearch}
      onInput={handleInput}
      onHandleSelectSearchItem={handleSelectSearchItem}
      autoSuggestItems={autoSuggestValues}
      autoSuggestDisplayCount={10}
      placeholder="Search DoneDeal"
      initialSearchValues={suggestedSearches(baseUrl)}
      tracking="GLOBAL"
      lastSearchData={lastSearchData}
      lastSearchDataTrackingValue="last_search_search_box"
    />
  );
};

export { GlobalSearchModal };
